import { type FunctionComponent, useEffect, useId, useRef } from 'react';
import { Button } from 'src/components/atoms/Button/Button';
import style from './ConfirmDialog.module.scss';

interface Props {
  title: string;
  text: string;
  confirm: string;
  cancel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog: FunctionComponent<Props> = ({ title, text, confirm, cancel, onConfirm, onCancel }) => {
  const id = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const stay = useRef<HTMLButtonElement>(null);

  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  useEffect(() => {
    const element = dialog.current;
    if (!element) {
      return;
    }
    const onBackdrop = (event: MouseEvent) => {
      if (event.target === element) {
        cancelRef.current();
      }
    };
    const opener = document.activeElement;
    element.addEventListener('click', onBackdrop);
    element.showModal();
    stay.current?.focus();
    return () => {
      element.removeEventListener('click', onBackdrop);
      if (opener instanceof HTMLElement) {
        opener.focus();
      }
    };
  }, []);

  return (
    <dialog
      ref={dialog}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-text`}
      className={style.dialog}
      onClose={onCancel}
    >
      <div className={style.body}>
        <h2 id={`${id}-title`} className={style.title}>
          {title}
        </h2>
        <p id={`${id}-text`} className={style.text}>
          {text}
        </p>
        <div className={style.actions}>
          <Button ref={stay} variant="secondary" onClick={onCancel}>
            {cancel}
          </Button>
          <Button onClick={onConfirm}>{confirm}</Button>
        </div>
      </div>
    </dialog>
  );
};
